import React, { useState, useEffect, useRef } from "react";
import { Search, Filter, Users, RefreshCw, User as UserIcon, Phone, Loader } from "lucide-react";
import NewLeadsPoolTable from "./NewLeadsPoolTable";
import Pagination from "../../components/Pagination";
import { useLanguage } from "../../contexts/LanguageContext";

const SEARCH_DEBOUNCE_MS = 300;

interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  countries?: string[];
  achievements?: string[]; // Task achievements for call operators
  alterCPAUserId?: number;
}

interface NewLeadsPoolProps {
  orders: any[];
  totalCount: number;
  onRefresh: () => void;
  onFetchLeads: (page: number, limit: number, search: string, task: string, lastOperatorId: string) => Promise<void>;
  currentUser: User;
  onTakeNextFromPool?: () => Promise<void>;
  hasActiveCall?: boolean;
  operators?: Array<{ userId: string; username: string }>;
}

const NewLeadsPool: React.FC<NewLeadsPoolProps> = ({
  orders,
  totalCount,
  onRefresh,
  onFetchLeads,
  currentUser,
  onTakeNextFromPool,
  hasActiveCall = false,
  operators = [],
}) => {
  const { t } = useLanguage();
  const [takingNext, setTakingNext] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [taskFilter, setTaskFilter] = useState<string>("all");
  const [lastOperatorFilter, setLastOperatorFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevFiltersRef = useRef({ debouncedSearchTerm, taskFilter, lastOperatorFilter });

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  // Debounce search: update debounced value after user stops typing
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      debounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm]);

  // Fetch data when pagination or (debounced) filters change; use page 1 when filters just changed
  useEffect(() => {
    const filtersChanged =
      prevFiltersRef.current.debouncedSearchTerm !== debouncedSearchTerm ||
      prevFiltersRef.current.taskFilter !== taskFilter ||
      prevFiltersRef.current.lastOperatorFilter !== lastOperatorFilter;
    if (filtersChanged) {
      prevFiltersRef.current = { debouncedSearchTerm, taskFilter, lastOperatorFilter };
      setCurrentPage(1);
    }
    const pageToFetch = filtersChanged ? 1 : currentPage;

    const fetchData = async () => {
      setLoading(true);
      try {
        await onFetchLeads(
          pageToFetch,
          itemsPerPage,
          debouncedSearchTerm,
          taskFilter,
          lastOperatorFilter === "all" ? "" : lastOperatorFilter
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentPage, itemsPerPage, debouncedSearchTerm, taskFilter, lastOperatorFilter, onFetchLeads]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleTakeNext = async () => {
    if (!onTakeNextFromPool || hasActiveCall || takingNext) return;
    setTakingNext(true);
    try {
      await onTakeNextFromPool();
    } finally {
      setTakingNext(false);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Users className="w-6 h-6 mr-2 text-blue-600" />
            {t('leads.availableLeadsPool')}
          </h2>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <span>{t('message.perPage')}</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(e.target.value)}
              disabled={loading}
              className="pl-2 pr-8 py-1.5 border border-gray-300 rounded-md text-sm bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
        </div>
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-600">
            {totalCount} {totalCount === 1 ? t('common.lead') : t('common.leads')} {t('message.available')}
          </p>
          {onTakeNextFromPool && (
            <button
              type="button"
              onClick={handleTakeNext}
              disabled={hasActiveCall || takingNext || totalCount === 0}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-[#264a3e] hover:bg-[#1a3329] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] disabled:opacity-50 disabled:cursor-not-allowed"
              title={hasActiveCall ? t('callCenter.callInProgress') : totalCount === 0 ? t('callCenter.noNewLeads') : t('callCenter.startNewCall')}
            >
              {takingNext ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  {t('callCenter.starting')}
                </>
              ) : (
                <>
                  <Phone className="w-4 h-4 mr-2" />
                  {t('callCenter.startNewCall')}
                </>
              )}
            </button>
          )}
        </div>
        <div className="mb-4">
          {currentUser.role === 'call-operator' && currentUser.achievements && currentUser.achievements.length > 0 && (
            <div className="mt-3 flex items-center space-x-2">
              <span className="text-xs font-medium text-gray-700">{t('callCenter.yourAchievements')}</span>
              <div className="flex flex-wrap gap-2">
                {currentUser.achievements.map((achievement) => {
                  const taskInfo = {
                    'update_address': { labelKey: 'callCenter.updateAddress', color: 'bg-blue-100 text-blue-800 border-blue-200' },
                    'sell_new_product': { labelKey: 'callCenter.sellNewProduct', color: 'bg-green-100 text-green-800 border-green-200' },
                    'collect_more_data': { labelKey: 'callCenter.collectMoreData', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
                    'new_dose': { labelKey: 'callCenter.newDose', color: 'bg-purple-100 text-purple-800 border-purple-200' },
                  }[achievement] || { labelKey: achievement, color: 'bg-gray-100 text-gray-800 border-gray-200' };
                  const label = taskInfo.labelKey ? t(taskInfo.labelKey) : String(achievement);
                  return (
                    <span
                      key={achievement}
                      className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${taskInfo.color}`}
                      title={t('callCenter.youCanTakeLeadsWithTask', { label })}
                    >
                      ✓ {label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={t('callCenter.searchLeads')}
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                disabled={loading}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {/* Task Filter */}
          <div className="sm:w-48">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Filter className="h-5 w-5 text-gray-400" />
              </div>
              <select
                value={taskFilter}
                onChange={(e) => setTaskFilter(e.target.value)}
                disabled={loading}
                className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="all">{t('callCenter.allTasks')}</option>
                <option value="update_address">{t('callCenter.updateAddress')}</option>
                <option value="sell_new_product">{t('callCenter.sellNewProduct')}</option>
                <option value="collect_more_data">{t('callCenter.collectMoreData')}</option>
                <option value="new_dose">{t('callCenter.newDose')}</option>
                <option value="custom">{t('callCenter.customTask')}</option>
              </select>
            </div>
          </div>

          {/* Last Operator Filter */}
          <div className="sm:w-48">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <UserIcon className="w-5 h-5 text-gray-400" />
              </div>
              <select
                value={lastOperatorFilter}
                onChange={(e) => setLastOperatorFilter(e.target.value)}
                disabled={loading}
                className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="all">{t('callCenter.allOperators')}</option>
                {operators.map((op) => (
                  <option key={op.userId} value={op.userId}>
                    {op.username || op.userId}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600">{t('callCenter.loadingLeads')}</span>
        </div>
      ) : (
        <NewLeadsPoolTable 
          orders={orders}
          currentUser={currentUser}
        />
      )}

      {/* Pagination - use shared component when there are multiple pages */}
      {totalCount > 0 && totalPages > 1 && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            showFirstLast
            maxVisiblePages={5}
          />
        </div>
      )}
    </div>
  );
};

export default NewLeadsPool;

